import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  InputAdornment,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { authService } from '../utils/api';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await authService.requestPasswordReset(email);
      setSuccess(true);
      toast.success('If this email exists in our system, a password reset link has been sent to your email address.');
    } catch (error) {
      const errorMessage = error.detail || error.message || 'Failed to request password reset. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: { xs: 2, md: 0 },
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Grid container sx={{ maxWidth: '500px' }}>
        <Grid item xs={12}>
          <Card 
            sx={{ 
              width: '100%', 
              borderRadius: 3,
              boxShadow: 3,
              py: 3,
              px: { xs: 3, sm: 6 }
            }}
          >
            <CardContent>
              <Button
                component={Link}
                to="/login"
                startIcon={<ArrowBack />}
                variant="text"
                sx={{ mb: 3, textTransform: 'none' }}
              >
                Back to Login
              </Button>

              <Typography variant="h4" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                Forgot Password?
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {success ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="success.main" sx={{ mb: 3 }}>
                    Check your email for password reset instructions.
                  </Typography>
                  <Button
                    component={Link}
                    to="/login"
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    Back to Login
                  </Button>
                </Box>
              ) : (
                <form onSubmit={handleSubmit}>
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ForgotPassword;

